import React, { useEffect, useRef, useState, forwardRef, useImperativeHandle } from "react";

const SEV_COLOR = {
  low: "#10B981",
  medium: "#F59E0B",
  high: "#E11D48",
  critical: "#E11D48",
};

/**
 * VideoTimeline
 * Renders the video with a hazard-marker timeline beneath it.
 * Each marker is a click-to-seek point with severity color.
 * Exposes a seekTo(t) imperative handle so parent components can drive playback.
 */
const VideoTimeline = forwardRef(function VideoTimeline(
  { videoUrl, hazards = [], contentType, sizeBytes },
  ref
) {
  const videoRef = useRef(null);
  const containerRef = useRef(null);
  const [duration, setDuration] = useState(0);
  const [currentTime, setCurrentTime] = useState(0);
  const [hoveredIdx, setHoveredIdx] = useState(null);

  const seek = (t) => {
    const v = videoRef.current;
    if (!v || !Number.isFinite(t)) return;
    v.currentTime = Math.max(0, Math.min(t, v.duration || t));
    v.play().catch(() => {});
    if (containerRef.current && typeof containerRef.current.scrollIntoView === "function") {
      try { containerRef.current.scrollIntoView({ behavior: "smooth", block: "start" }); } catch (e) {}
    }
  };

  useImperativeHandle(ref, () => ({ seekTo: seek }), []);

  const onLoaded = () => {
    const v = videoRef.current;
    if (v && !Number.isNaN(v.duration)) setDuration(v.duration);
  };
  const onTime = () => {
    const v = videoRef.current;
    if (v) setCurrentTime(v.currentTime);
  };

  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;
    v.addEventListener("loadedmetadata", onLoaded);
    v.addEventListener("timeupdate", onTime);
    return () => {
      v.removeEventListener("loadedmetadata", onLoaded);
      v.removeEventListener("timeupdate", onTime);
    };
  }, [videoUrl]);

  const markers = hazards
    .map((h, i) => ({ ...h, _i: i }))
    .filter((h) => h.start_seconds !== null && h.start_seconds !== undefined && !Number.isNaN(Number(h.start_seconds)));

  const pct = (t) => (duration > 0 ? Math.max(0, Math.min(100, (t / duration) * 100)) : 0);

  return (
    <div className="border border-ink-900" data-testid="video-timeline" ref={containerRef}>
      <div className="grid grid-cols-3 bg-ink-900 text-white p-3 font-mono text-[10px] uppercase tracking-[0.3em]">
        <span>VIDEO · SOURCE</span>
        <span className="text-center">{contentType}</span>
        <span className="text-right">{sizeBytes ? `${(sizeBytes / (1024 * 1024)).toFixed(1)} MB` : ""}</span>
      </div>

      {videoUrl ? (
        <video
          ref={videoRef}
          src={videoUrl}
          controls
          className="w-full bg-black max-h-[520px]"
          data-testid="analysis-video"
        />
      ) : (
        <div className="aspect-video bg-black flex items-center justify-center text-white/40 font-mono text-xs uppercase tracking-[0.3em]">
          Loading video…
        </div>
      )}

      <div className="bg-ink-900 text-white p-4">
        <div className="flex items-center justify-between mb-3 font-mono text-[10px] uppercase tracking-[0.3em] text-white/60">
          <span>HAZARD TIMELINE · {markers.length} markers</span>
          <span>{formatTime(currentTime)} / {formatTime(duration)}</span>
        </div>

        <div className="relative h-12 bg-white/5 border border-white/10">
          <div
            className="absolute inset-0 cursor-pointer"
            onClick={(e) => {
              if (!duration) return;
              const rect = e.currentTarget.getBoundingClientRect();
              const x = (e.clientX - rect.left) / rect.width;
              seek(x * duration);
            }}
            data-testid="timeline-track"
          />
          <div
            className="absolute top-0 bottom-0 w-[2px] bg-white pointer-events-none"
            style={{ left: `${pct(currentTime)}%` }}
          />
          {markers.map((m) => {
            const left = pct(Number(m.start_seconds));
            const w = m.end_seconds ? Math.max(2, pct(Number(m.end_seconds)) - left) : 0;
            const color = SEV_COLOR[m.severity] || "#F59E0B";
            return (
              <React.Fragment key={m._i}>
                {w > 0 && (
                  <div
                    className="absolute top-0 bottom-0 opacity-25"
                    style={{ left: `${left}%`, width: `${w}%`, background: color }}
                  />
                )}
                <button
                  type="button"
                  onClick={() => seek(Number(m.start_seconds))}
                  onMouseEnter={() => setHoveredIdx(m._i)}
                  onMouseLeave={() => setHoveredIdx(null)}
                  className="absolute top-0 bottom-0 w-3 -ml-[6px] flex items-center justify-center group"
                  style={{ left: `${left}%` }}
                  aria-label={`Seek to ${m.name}`}
                  data-testid={`timeline-marker-${m._i}`}
                >
                  <span
                    className="block w-2 h-2 group-hover:scale-150 transition-transform"
                    style={{ background: color, boxShadow: `0 0 0 2px ${color}33` }}
                  />
                </button>
              </React.Fragment>
            );
          })}
        </div>

        {markers.length > 0 && (
          <div className="mt-3 flex flex-wrap gap-1.5">
            {markers.map((m) => {
              const color = SEV_COLOR[m.severity] || "#F59E0B";
              const isHover = hoveredIdx === m._i;
              return (
                <button
                  key={m._i}
                  type="button"
                  onClick={() => seek(Number(m.start_seconds))}
                  onMouseEnter={() => setHoveredIdx(m._i)}
                  onMouseLeave={() => setHoveredIdx(null)}
                  className={`inline-flex items-center gap-2 px-2 py-1 border ${isHover ? "border-white" : "border-white/20"} bg-white/5 hover:bg-white/10 text-left transition-colors`}
                  data-testid={`timeline-chip-${m._i}`}
                >
                  <span className="w-1.5 h-1.5" style={{ background: color }} />
                  <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-white/70">{formatTime(Number(m.start_seconds))}</span>
                  <span className="text-xs text-white truncate max-w-[180px]">{m.name}</span>
                </button>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
});

function formatTime(s) {
  if (!Number.isFinite(s) || s < 0) return "0:00";
  const m = Math.floor(s / 60);
  const sec = Math.floor(s % 60).toString().padStart(2, "0");
  return `${m}:${sec}`;
}

export default VideoTimeline;
